package com.siriusxm.pia.views.unifiedaggregator.relationships

import androidx.compose.runtime.*
import com.siriusxm.cmc.utils.encodeURIComponent
import com.siriusxm.pia.components.*
import com.siriusxm.pia.rest.unifiedaggregator.Entity
import com.siriusxm.pia.rest.unifiedaggregator.asEntities
import com.siriusxm.pia.views.unifiedaggregator.AggregatorService
import com.siriusxm.pia.views.unifiedaggregator.AggregatorStyles
import com.siriusxm.pia.views.unifiedaggregator.entityListItem
import com.siriusxm.cmc.error
import com.siriusxm.pia.CapabilityQuery
import com.siriusxm.unifiedcontent.console.aggregator.RelationshipOperation
import com.siriusxm.unifiedcontent.console.aggregator.allowsRelationship
import com.siriusxm.unifiedcontent.console.aggregator.consoleProducer
import com.siriusxm.unifiedcontent.console.aggregator.intakeErrorMessage
import com.siriusxm.unifiedcontent.console.aggregator.relationshipCreateAffordance
import com.siriusxm.unifiedcontent.console.aggregator.relationshipUpdateCapability
import com.siriusxm.unifiedcontent.console.aggregator.rememberCapabilities
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

/**
 * A view to show all relationships that this entity is a part of: a relationship-type selector, the
 * related entities (paged), a "New Relationship" action, and a remove icon per related entity.
 *
 * Relationship edits go out as the console's own producer (UCA-1152), and the aggregator authorizes
 * the user through AVP (`UnifiedContentAggregator_RelationshipUpdate`, UCA-1151) with no admin bypass.
 * So "New Relationship" is offered when the console producer allows the relationship type (the
 * ceiling the API enforces) and AVP allows CREATE with this entity on its side and the other side not
 * chosen yet; the editor checks the exact pair again before submitting. Each remove icon is shown only when AVP allows REMOVE for that exact pair, asked in one batch
 * for the visible rows. Both only hide controls; the aggregator decides.
 */
@Composable
fun entityRelationships(context: AggregatorService, entity: Entity) {
    val scope = rememberCoroutineScope()
    val availableRelationships = context.relationships.filter {
        (it.subjectType.name == entity.type || it.objectType.name == entity.type)
                && it.subjectType.name != it.objectType.name
    }
    var selectedRelationship by remember { mutableStateOf(availableRelationships.firstOrNull()) }
    var currentView by remember { mutableStateOf<List<Entity>?>(null) }

    if (availableRelationships.isEmpty()) {
        messageBox("This entity type is not eligible for any relationships.")
        return
    }

    val consoleProducer = consoleProducer(context.producers, context.context.configuration.clientId)
    val selectedIsEditable = selectedRelationship?.let {
        it.editable && consoleProducer?.allowsRelationship(it.id) == true
    } ?: false

    /** The (subject, object) pair for a relationship between this entity and [sibling]. */
    fun pairWith(sibling: Entity): Pair<String, String> =
        if (selectedRelationship?.subjectType?.name == entity.type) entity.id to sibling.id
        else sibling.id to entity.id

    // One REMOVE question per visible related entity, resolved in a single batched AVP call.
    val removeQueries: Map<String, CapabilityQuery> = remember(selectedRelationship, selectedIsEditable, currentView) {
        val relationshipId = selectedRelationship?.id
        if (relationshipId == null || !selectedIsEditable) {
            emptyMap()
        } else {
            currentView.orEmpty().associate { sibling ->
                val (subjectId, objectId) = pairWith(sibling)
                sibling.id to relationshipUpdateCapability(
                    relationshipId, RelationshipOperation.REMOVE, subjectId, objectId
                )
            }
        }
    }
    // Whether to offer "New Relationship": CREATE with this entity on its side and the other side
    // not chosen yet. Asked in the same batch as the REMOVE questions.
    val createQuery: CapabilityQuery? = remember(selectedRelationship, selectedIsEditable) {
        val relationship = selectedRelationship
        if (relationship == null || !selectedIsEditable) {
            null
        } else if (relationship.subjectType.name == entity.type) {
            relationshipCreateAffordance(relationship.id, subjectId = entity.id)
        } else {
            relationshipCreateAffordance(relationship.id, objectId = entity.id)
        }
    }
    val decisions by context.context.rememberCapabilities(removeQueries.values + listOfNotNull(createQuery))
    fun canRemove(sibling: Entity): Boolean =
        removeQueries[sibling.id]?.let { decisions?.get(it) } == true
    val canCreate = createQuery?.let { decisions?.get(it) } == true

    suspend fun removeRelationship(sibling: Entity) {
        val relationshipId = selectedRelationship?.id ?: return
        val (subject, obj) = pairWith(sibling)

        try {
            context.clientApi.removeRelationship(relationshipId, subject, obj)
            currentView = currentView?.filter {
                it.id != sibling.id
            }
        } catch (e: CancellationException) {
            throw e
        } catch (e: Throwable) {
            val error = intakeErrorMessage("remove this relationship", e)
            context.notifications.error(error.message, error.description)
        }
    }

    box({
        paddedContent = true
        this.header {
            Div({
                style { paddingRight(1.em) }
            }) {
                Div {
                    Select({
                        style { width(100.percent) }
                        onChange { update ->
                            selectedRelationship = availableRelationships.find { it.id == update.value }
                            currentView = null
                        }
                    }) {
                        availableRelationships.sortedBy { it.name.lowercase() }.forEach {
                            Option(it.id, {
                                if (it == selectedRelationship) selected()
                            }) {
                                Text(it.name)
                            }
                        }
                    }
                }

                selectedRelationship?.let { relationship ->
                    Div({
                        style {
                            marginTop(1.em)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                        }
                    }) {
                        Div({ style { flex(1) } }) {
                            Text(relationship.description)
                        }
                        Div {
                            LazyPaginationControl(
                                key = selectedRelationship?.id ?: "none",
                                maxPages = 100,
                                updater = { entities ->
                                    currentView = entities
                                },
                                fetcher = { token ->
                                    val isSubject = relationship.subjectType.name == entity.type
                                    val response = if (isSubject) {
                                        context.clientApi.getRelationshipObjects(
                                            relationshipId = relationship.id,
                                            entityId = entity.id,
                                            descending = true,
                                            paginationToken = token,
                                            limit = 20
                                        )
                                    } else {
                                        context.clientApi.getRelationshipSubjects(
                                            relationshipId = relationship.id,
                                            entityId = entity.id,
                                            descending = true,
                                            paginationToken = token,
                                            limit = 20
                                        )
                                    }
                                    PageContents(response.entities.asEntities(), response.paginationToken)
                                }
                            )
                        }
                    }
                }
            }
        }
        if (canCreate) {
            action("New Relationship", true) {
                // Each segment is encoded *before* the return path is assembled, and the assembled
                // path is then encoded once as a query value. Encoding only the assembled string
                // would conflate slashes inside the entity id with the structural slashes around it,
                // so a single decode on the way back could not tell them apart.
                val returnPath = "entity/${encodeURIComponent(entity.id)}/relationships"
                context.navigate(
                    "relationships/edit?relationship=${
                        encodeURIComponent(
                            selectedRelationship!!.id
                        )
                    }&entity=${encodeURIComponent(entity.id)}&return=${encodeURIComponent(returnPath)}"
                )
            }
        }
    }) {
        selectedRelationship?.let { relationship ->
            if (currentView == null) {
                spinner()
            }
            currentView?.let { entities ->
                if (entities.isNotEmpty()) {
                    Div({ classes(AggregatorStyles.entityList) }) {
                        var entityPendingDelete by remember { mutableStateOf<Entity?>(null) }

                        entities.forEach { entity ->
                            entityListItem(
                                context, entity, true, false, false,
                                actions = if (canRemove(entity)) {
                                    {
                                        svgIcon(SVGIcons.trash) {
                                            size = 18.px
                                            title = "Remove relationship"
                                            action {
                                                entityPendingDelete = entity
                                            }
                                        }
                                    }
                                } else null)
                        }

                        entityPendingDelete?.let { pending ->
                            confirmationDialog(
                                "Remove relationship",
                                "Remove the ${selectedRelationship?.name} relationship with ${pending.label}?",
                                "Remove"
                            ) { confirmed ->
                                if (confirmed) {
                                    scope.launch {
                                        removeRelationship(pending)
                                        entityPendingDelete = null
                                    }
                                } else {
                                    entityPendingDelete = null
                                }
                            }
                        }
                    }
                } else {
                    boxMessage("There are no relationships")
                }
            }
        }
    }
}
